/**
 * Embed/extract the TGPE clipboard payload inside an HTML comment so it
 * survives the text/html clipboard channel. Browsers expose text/html
 * synchronously via DataTransfer.getData(), unlike `web `-prefixed custom
 * MIME types. The comment is invisible to all HTML consumers (Telegram,
 * Notion, Apple Mail, etc.) so this side-channel is non-disruptive.
 *
 * Format:  <!--TGPE:1:<base64-utf8(json)>-->
 *
 * The `1:` is a tag/version so we can change the encoding later (e.g.
 * compress with gzip+base64) without confusing older builds.
 */

const COMMENT_PREFIX = '<!--TGPE:1:';
const COMMENT_SUFFIX = '-->';
const COMMENT_REGEX = /<!--TGPE:1:([A-Za-z0-9+/=]+)-->/;

/**
 * Wrap a JSON payload string in an HTML comment, base64-encoded so it
 * survives newlines and odd characters. Unicode-safe.
 */
export function encodeTGPEHtmlComment(payloadJson: string): string {
  const utf8 = new TextEncoder().encode(payloadJson);
  let binary = '';
  for (const byte of utf8) binary += String.fromCharCode(byte);
  const base64 = typeof btoa === 'function'
    ? btoa(binary)
    : Buffer.from(binary, 'binary').toString('base64');
  return `${COMMENT_PREFIX}${base64}${COMMENT_SUFFIX}`;
}

/**
 * Search the given HTML string for an embedded TGPE comment and return the
 * decoded payload JSON. Returns null when no comment is present or the
 * comment is unparseable.
 */
export function extractTGPEHtmlComment(html: string): string | null {
  const match = COMMENT_REGEX.exec(html);
  if (!match) return null;
  const base64 = match[1];
  if (!base64) return null;
  try {
    const binary = typeof atob === 'function'
      ? atob(base64)
      : Buffer.from(base64, 'base64').toString('binary');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}
